"use client";

import React, { useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { VirtualScrollItem } from "@/lib/types/virtual-scroll";
import { MobileCardErrorBoundary } from "./ErrorBoundaries";
import { SimpleTableMobileCard } from "./SimpleTableMobileCard";
import type { BaseEntity, SimpleTableConfig, FieldValue, EntityHooks } from "./types";
import {
  CRM_VIRTUAL_OVERSCAN,
  ESTIMATE_MOBILE_ENTITY_CARD_PX,
  MOBILE_CARD_LIST_GAP_PX,
} from "@/lib/virtualization/crm-virtual-scroll-constants";

export interface VirtualizedMobileEntityCardsProps<T extends BaseEntity> {
  data: T[];
  config: SimpleTableConfig<T>;
  visibleColumnIds?: string[];
  hooks?: EntityHooks<T>;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
  onInlineEdit?: (payload: Record<string, FieldValue>) => Promise<void>;
  onView: (item: T) => void;
  selectedItems: string[];
  onItemSelect: (itemId: string, selected: boolean) => void;
  enableSelection: boolean;
  /** When provided, the card's actions button opens a mobile bottom sheet instead of a dropdown. */
  onOpenActions?: (item: T) => void;
}

/**
 * Mobile card list virtualized with @tanstack/react-virtual (same stack as DataTable-modular).
 * Scroll container is bounded so only visible cards mount.
 */
export function VirtualizedMobileEntityCards<T extends BaseEntity>({
  data,
  config,
  visibleColumnIds,
  hooks,
  onEdit,
  onDelete,
  onInlineEdit,
  onView,
  selectedItems,
  onItemSelect,
  enableSelection,
  onOpenActions,
}: VirtualizedMobileEntityCardsProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    getItemKey: (index: number) => data[index]?.id ?? index,
    estimateSize: () => ESTIMATE_MOBILE_ENTITY_CARD_PX,
    // Cards have variable height by entity/field layout; measure to prevent overlap.
    measureElement: (el: Element) => el.getBoundingClientRect().height,
    gap: MOBILE_CARD_LIST_GAP_PX,
    overscan: CRM_VIRTUAL_OVERSCAN,
  });

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <div
      ref={parentRef}
      className="max-h-[min(70vh,640px)] min-h-[120px] overflow-y-auto rounded-md border border-border/60 bg-muted/20"
      aria-busy={false}
    >
      <div
        style={{
          height: `${totalSize}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {items.map((virtualRow: VirtualScrollItem) => {
          const item = data[virtualRow.index];
          if (!item) return null;
          return (
            <div
              key={item.id}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <MobileCardErrorBoundary>
                <SimpleTableMobileCard
                  item={item}
                  config={config}
                  visibleColumnIds={visibleColumnIds}
                  hooks={hooks}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onInlineEdit={onInlineEdit}
                  onView={onView}
                  isSelected={selectedItems.includes(item.id)}
                  // validate-memo-stable-props-allow: per-row handler in list render
                  onSelect={(selected) => onItemSelect(item.id, selected)}
                  enableSelection={enableSelection}
                  onOpenActions={onOpenActions}
                />
              </MobileCardErrorBoundary>
            </div>
          );
        })}
      </div>
    </div>
  );
}
